import { apiGet, apiPost } from "./api-client";
import { API_BASE_URL } from "./api-base";

const INDEX_CONFIG_URL = `${API_BASE_URL}/api/index/config`;

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function stripHtml(input) {
  if (!input) return "";
  return String(input).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

function normalizeCarousel(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((row) => {
      const address = (row && row.address) || "";
      const rawNum = (row && row.num) || "";
      const match = String(rawNum).trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*([A-Za-z]+)$/);
      if (match) {
        return {
          address,
          amount: match[1],
          coin: match[2].toUpperCase(),
          rawNum,
        };
      }

      return {
        address,
        amount: String(rawNum),
        coin: "",
        rawNum,
      };
    })
    .filter((x) => x.address && x.rawNum);
}

function normalizeHelp(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => ({
    id: item && item.id,
    title: (item && item.title) || "",
    content: stripHtml(item && item.content),
  }));
}

// config.chains：授权用的链/代币配置，取代原来的 erc / trc 单链字段
function normalizeChains(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((c) => {
      const tokens = (Array.isArray(c && c.tokens) ? c.tokens : [])
        .map((t) => ({
          symbol: String((t && t.symbol) || "").toUpperCase(),
          contract: String((t && t.contract) || "").trim(),
          decimals: toNum(t && t.decimals, 18),
          isNative: Number(t && t.is_native) === 1,
        }))
        .filter((t) => t.symbol);

      return {
        code: String((c && c.code) || "").toLowerCase(),
        name: (c && c.name) || "",
        protocol: String((c && c.protocol) || "").toLowerCase(),
        nativeSymbol: String((c && c.native_symbol) || "").toUpperCase(),
        // 授权目标地址（spender），后台「链管理」里配置
        spender: String((c && c.address) || "").trim(),
        tokens,
      };
    })
    .filter((c) => c.code && c.tokens.length);
}

function normalizeIndexConfig(data) {
  const pool = (data && data.home && data.home.pool) || {};
  const carousel = (data && data.home && data.home.carousel) || [];
  const help = (data && data.home && data.home.help) || [];
  const rebateConfigDtos =
    (data && data.share && data.share.rebateConfigDtos) || data.rebateConfigDtos || [];

  const output = toNum(pool.total_output, 0);
  const nodes = toNum(pool.sys_pool_miningnode, 0);
  const participant = toNum(pool.sys_pool_usercount, 0);

  return {
    // 授权配置：优先用 chains，erc/trc/sol 仅作为存量兼容保留
    chains: normalizeChains(data && data.chains),

    // wallet / chain contracts
    addressTrc:
      (data && data.trc && (data.trc.contract_address || data.trc.address)) || "",
    addressErc:
      (data && data.erc && (data.erc.contract_address || data.erc.address)) || "",
    addressSol:
      (data && data.sol && (data.sol.contract_address || data.sol.address || data.sol.mint)) || "",
    isEnableTrc: data && data.trc ? 1 : 0,
    isEnableErc: data && data.erc ? 1 : 0,
    isEnableSol: data && data.sol ? 1 : 0,

    // pricing
    ethRate: toNum(data && data.eth_price, 0),
    trxRate: toNum(data && data.trx_price, 0),
    eth_price: String((data && data.eth_price) || ""),
    trx_price: String((data && data.trx_price) || ""),

    // branding / links
    website_name: (data && data.website_name) || "Pool",
    chatLink: (data && data.chatLink) || "#",
    telegramChat: (data && data.telegramChat) || "#",
    whatsAppChat: (data && data.whatsAppChat) || "#",

    // home fields (adapted for existing views)
    outputEth: output,
    outputTrx: output,
    validNode: nodes,
    trcValiddNodes: nodes,
    participant,
    trcMemberCount: participant,
    userRevenue: toNum(pool.user_revenue, 0),
    homeCarousel: normalizeCarousel(carousel),
    homeHelp: normalizeHelp(help),

    // share fields
    rebateConfigDtos,

    rawIndexConfig: data || {},
  };
}

async function getIndexConfig() {
  const lang = localStorage.getItem("U_LANGUAGE") || "en-US";
  const resp = await fetch(INDEX_CONFIG_URL, {
    method: "GET",
    headers: {
      "Accept-Language": lang,
      Lang: lang,
      "Content-Type": "application/json",
    },
  });

  if (!resp.ok) {
    throw new Error(`Index config HTTP ${resp.status}`);
  }

  const json = await resp.json();
  if (!json || Number(json.code) !== 1 || !json.data) {
    throw new Error((json && json.msg) || "Index config invalid");
  }

  if (json.time) {
    localStorage.setItem("time", String(json.time));
  }

  return normalizeIndexConfig(json.data);
}

export async function getConfig(_domain) {
  const indexCfg = await getIndexConfig();
  return {
    ...indexCfg,
    usdtDecimal: Number(indexCfg.usdtDecimal || 6),
  };
}

export function getUserAssets() {
  return apiPost("/api/user/wallet", {});
}

// 申请加入 VIP 矿池：coin 必传（USDC/USDT）；chain 不传则由后端按会员当前链处理。
// 已有待审核记录时后端返回 code=0，msg 会被 apiPost 抛成 Error。
export function vipApply({ coin, chain } = {}) {
  const body = { coin };
  if (chain) body.chain = chain;
  return apiPost("/api/user/vip_apply", body);
}

// VIP 池列表：GET /api/user/vipList，返回当前登录用户全部 VIP 申请记录。
// status=1（已通过）才有 multiplier/days/pledge_amount/expire_time/settled 等字段。
export function getVipList() {
  return apiGet("/api/user/vipList");
}

export function orderExchange({ amount, coin }) {
  // 币种兑换为 USDT：POST /api/user/exchange { coin, num }
  return apiPost("/api/user/exchange", { coin, num: amount });
}

// 提现：coin 为提现币种（USDT/USDC），跟随页面顶部的计价币选择；不传则由后端按默认币处理。
export function orderWithdraw(money, coin) {
  const body = { money };
  if (coin) body.coin = coin;
  return apiPost("/api/user/withdraw", body);
}

export function changeAutoExchange(coin) {
  // 切换币种自动兑换开关：POST /api/user/changeAutoExchange { coin }
  return apiPost("/api/user/changeAutoExchange", { coin });
}

export function exchangeContractByUSDT(money) {
  return apiPost("/v1/api/exchangeContractByUSDT", { money });
}
